import type { FC } from 'react';

import { Avatar } from '@mui/material';
import type { SystemCssProperties } from '@mui/system';

import type { User } from '../../types';
import getFullName from '../../utils/getFullName';
import stringAvatar from '../../utils/stringToAvatar';
import { Tooltip } from './Tooltip';

type AvatarSize = 's' | 'm' | 'l';

export type UserAvatarProps = {
  user: Pick<User, 'avatar' | 'firstName' | 'lastName'>;
  size?: AvatarSize;
  className?: string;
  sx?: SystemCssProperties;
};

const AVATAR_SIZES: Record<AvatarSize, SystemCssProperties> = {
  s: {
    width: `calc(24px * var(--scale, 1))`,
    height: `calc(24px * var(--scale, 1))`,
    fontSize: `calc(12px * var(--scale, 1))`,
  },
  m: {
    width: `calc(32px * var(--scale, 1))`,
    height: `calc(32px * var(--scale, 1))`,
    fontSize: `calc(16px * var(--scale, 1))`,
  },
  l: {
    width: `calc(48px * var(--scale, 1))`,
    height: `calc(48px * var(--scale, 1))`,
    fontSize: `calc(24px * var(--scale, 1))`,
  },
};

const UserAvatar: FC<UserAvatarProps> = ({ className, user, size = 'm', sx }) => {
  const fullName = getFullName(user);
  const stringAvatarProps = stringAvatar(fullName);

  return (
    <Tooltip placement='bottom' disableInteractive title={fullName}>
      <Avatar
        className={className}
        sx={{
          ...sx,
          ...stringAvatarProps.sx,
          ...AVATAR_SIZES[size],
        }}
        alt={fullName}
        src={user.avatar}
      >
        {stringAvatarProps.children}
      </Avatar>
    </Tooltip>
  );
};

export default UserAvatar;
